import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { MagisterLiveTileContent } from "./MagisterLiveTileContent";
import { navigateToMagisterApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { MagisterLiveInformationProvider } from "./provider";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type MagisterTileContentProps = {
	renderState: TileRenderState<AppType.Magister>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: MagisterLiveInformationProvider;
};

export const MagisterTileContent = (props: MagisterTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useMagisterTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<MagisterLiveTileContent
				connectionId={settings.connectionId}
				destinationWindow={settings.destinationWindow}
				tabs={settings.tabs}
				provider={provider}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
MagisterTileContent.displayName = "MagisterTileContent";

export function useMagisterTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Magister>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: { destinationWindow }
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();

				navigateToMagisterApp({
					id: environmentAppId,
					destinationWindow
				});
			},
			[
				destinationWindow,
				environmentAppId,
				registerTileUsage
			]
		)
	);
}
